<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { CheckCircle2, XCircle, PauseCircle, Trash2 } from 'lucide-vue-next'
import Badge from '@/components/ui/Badge.vue'
import Empty from '@/components/ui/Empty.vue'
import Skeleton from '@/components/ui/Skeleton.vue'
import { formatBytes, formatRelativeTime } from '@/utils/format'
import type { CompletedTask } from '@/api/stopped'

interface Props {
  items: CompletedTask[]
  loading?: boolean
  /** completion timestamps by gid, tracked client-side (backend has none) */
  completedAt?: Record<string, number>
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  completedAt: () => ({}),
})

const emit = defineEmits<{ (e: 'select', task: CompletedTask): void }>()

const { t, locale } = useI18n()

function statusMeta(status: string) {
  switch (status) {
    case 'complete':
      return { icon: CheckCircle2, variant: 'success' as const, key: 'dashboard.statusComplete' }
    case 'error':
      return { icon: XCircle, variant: 'danger' as const, key: 'dashboard.statusError' }
    case 'paused':
      return { icon: PauseCircle, variant: 'warning' as const, key: 'dashboard.statusPaused' }
    default:
      return { icon: Trash2, variant: 'neutral' as const, key: 'dashboard.statusRemoved' }
  }
}

const rows = computed(() =>
  props.items.map((task) => {
    const meta = statusMeta(task.status)
    const ts = props.completedAt[task.gid]
    return {
      task,
      icon: meta.icon,
      variant: meta.variant,
      statusLabel: t(meta.key),
      size: formatBytes(task.totalLength || task.completedLength),
      when: ts ? formatRelativeTime(ts, locale.value) : t('dashboard.justTracked'),
    }
  }),
)
</script>

<template>
  <div>
    <div v-if="loading" class="space-y-3">
      <div v-for="i in 3" :key="i" class="flex items-center gap-3">
        <Skeleton width="1rem" height="1rem" rounded="full" />
        <Skeleton width="40%" height="0.875rem" />
        <Skeleton width="4rem" height="0.875rem" class="ml-auto" />
      </div>
    </div>

    <Empty
      v-else-if="rows.length === 0"
      :title="t('dashboard.noCompleted')"
      :description="t('dashboard.noCompletedHint')"
    />

    <ul v-else class="divide-y divide-border">
      <li v-for="row in rows" :key="row.task.gid">
        <button
          type="button"
          class="flex w-full items-center gap-3 rounded-md px-1 py-2.5 text-left transition-colors duration-fast hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          @click="emit('select', row.task)"
        >
          <component
            :is="row.icon"
            :size="16"
            class="shrink-0"
            :class="{
              'text-success': row.variant === 'success',
              'text-danger': row.variant === 'danger',
              'text-warning': row.variant === 'warning',
              'text-fg-subtle': row.variant === 'neutral',
            }"
          />

          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm text-fg" :title="row.task.path || row.task.name">
              {{ row.task.name }}
            </span>
            <span v-if="row.task.errorMessage" class="block truncate text-xs text-danger">
              {{ row.task.errorMessage }}
            </span>
          </span>

          <Badge v-if="row.variant !== 'success'" :variant="row.variant" size="sm">
            {{ row.statusLabel }}
          </Badge>

          <span class="shrink-0 tabular-nums text-xs text-fg-muted">{{ row.size }}</span>
          <span class="hidden shrink-0 text-xs text-fg-subtle sm:inline">{{ row.when }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>
